import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CartItem } from '../models/cart.model';
import { PosCustomer } from '../models/pos-catalog.model';
import { cleAppareil } from '@stockflow/offline-core';

const DB_NAME = 'stockflow_pos';
const DB_VERSION = 1;
const STORE_NAME = 'cart';
function getCartKey(): string {
    try {
        const companyId = localStorage.getItem('selectedCompanyId') || 'default';
        return 'current_cart_' + companyId;
    } catch {
        return 'current_cart_default';
    }
}

/** État du panier persisté dans IndexedDB (chiffré AES-256-GCM). */
export interface PersistedCartState {
    items: CartItem[];
    discount: number;
    customer: PosCustomer | null;
    taxes: { name: string; rate: number; amount: number }[];
    savedAt: number;
}

@Injectable({
    providedIn: 'root'
})
export class CartPersistenceService {
    private platformId = inject(PLATFORM_ID);
    private db: IDBDatabase | null = null;
    private cryptoKey: CryptoKey | null = null;

    private get isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    /** Initialise la connexion IndexedDB et la clé de chiffrement. */
    async init(): Promise<void> {
        if (!this.isBrowser) return;
        await this.openDb();
        await this.getOrCreateKey();
    }

    private openDb(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME);
                }
            };
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * La clé du panier est la clé d'APPAREIL : non extractible, rangée à part.
     *
     * Elle était créée `extractable: true`, exportée en brut et écrite sous
     * `crypto_key` DANS LE MEME magasin que les paniers : quiconque lisait la base
     * lisait la clé avec — du chiffrement pour la forme. Les paniers déjà écrits
     * ne se déchiffrent pas avec la nouvelle clé : ils sont retirés avec l'ancienne
     * clé (un panier expire de toute façon après 24 h).
     *
     * Clé d'appareil indisponible (IndexedDB bloqué) : le panier n'est pas
     * persisté — il se reconstruit, une clé lisible à côté des données non.
     */
    private async getOrCreateKey(): Promise<void> {
        await this.retirerAncienneCle();
        this.cryptoKey = await cleAppareil.obtenir();
    }

    private async retirerAncienneCle(): Promise<void> {
        if (!(await this.idbGet('crypto_key'))) return;
        await this.idbDelete('crypto_key');
        for (const cle of await this.idbCles()) {
            if (cle.startsWith('current_cart_')) await this.idbDelete(cle);
        }
    }

    private idbCles(): Promise<string[]> {
        return new Promise((resolve, reject) => {
            const req = this.db!.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAllKeys();
            req.onsuccess = () => resolve(req.result.map(String));
            req.onerror = () => reject(req.error);
        });
    }

    /** Chiffre et persiste l'état du panier dans IndexedDB. */
    async save(state: PersistedCartState): Promise<void> {
        if (!this.isBrowser || !this.db || !this.cryptoKey) return;

        try {
            const json = JSON.stringify(state);
            const encoded = new TextEncoder().encode(json);
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encrypted = await crypto.subtle.encrypt(
                { name: 'AES-GCM', iv }, this.cryptoKey, encoded
            );

            const payload = new Uint8Array(iv.length + encrypted.byteLength);
            payload.set(iv, 0);
            payload.set(new Uint8Array(encrypted), iv.length);

            await this.idbPut(getCartKey(), payload.buffer);
        } catch {
            // Échec de sauvegarde silencieux — le panier sera perdu au rechargement
        }
    }

    /** Déchiffre et restaure l'état du panier depuis IndexedDB. */
    async load(): Promise<PersistedCartState | null> {
        if (!this.isBrowser || !this.db || !this.cryptoKey) return null;

        try {
            const stored = await this.idbGet(getCartKey()) as ArrayBuffer | null;
            if (!stored) return null;

            const payload = new Uint8Array(stored);
            const iv = payload.slice(0, 12);
            const ciphertext = payload.slice(12);

            const decrypted = await crypto.subtle.decrypt(
                { name: 'AES-GCM', iv }, this.cryptoKey, ciphertext
            );

            const json = new TextDecoder().decode(decrypted);
            return JSON.parse(json) as PersistedCartState;
        } catch {
            return null;
        }
    }

    /** Supprime le panier persisté. */
    async clear(): Promise<void> {
        if (!this.isBrowser || !this.db) return;
        try {
            await this.idbDelete(getCartKey());
        } catch {
            // Échec silencieux
        }
    }

    private idbPut(key: string, value: ArrayBuffer): Promise<void> {
        return new Promise((resolve, reject) => {
            const tx = this.db!.transaction(STORE_NAME, 'readwrite');
            tx.objectStore(STORE_NAME).put(value, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    private idbGet(key: string): Promise<ArrayBuffer | null> {
        return new Promise((resolve, reject) => {
            const tx = this.db!.transaction(STORE_NAME, 'readonly');
            const req = tx.objectStore(STORE_NAME).get(key);
            req.onsuccess = () => resolve((req.result as ArrayBuffer) ?? null);
            req.onerror = () => reject(req.error);
        });
    }

    private idbDelete(key: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const tx = this.db!.transaction(STORE_NAME, 'readwrite');
            tx.objectStore(STORE_NAME).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
}
